import { AUTH_ORIGINS } from "./account-links";

const SITES = Object.freeze({ global: "https://blenderbridge.com", ru: "https://blenderbridge.ru" });
const LANGUAGES = new Set(["en","ru","de","fr","es","pt","tr","ja","zh","ko","ar","hi","bn","id","vi"]);
/*
 * Метки кампании переезжают вместе с посетителем: короткая ссылка из соцсети
 * ведёт на .com, и без них человек из России приходил бы на .ru «ниоткуда».
 * Только эти пять полей и только в том же виде, что принимает сбор статистики.
 */
const CAMPAIGN_FIELDS = ["utm_source","utm_medium","utm_campaign","utm_content","utm_term"] as const;
const CAMPAIGN_VALUE = /^[\p{L}\p{N} ._-]{1,64}$/u;

export interface RegionRoutingPort {
  readonly location: Pick<Location, "hostname" | "pathname" | "search" | "hash">;
  language(): string;
  lookup(url: string): Promise<unknown>;
  replace(url: string): void;
}

export async function routeMarketingRegion(port: RegionRoutingPort): Promise<void> {
  const { hostname, pathname, search, hash } = port.location;
  const current = ["blenderbridge.com","www.blenderbridge.com"].includes(hostname) ? "global"
    : ["blenderbridge.ru","www.blenderbridge.ru"].includes(hostname) ? "ru" : null;
  if (!current || !["/","/index.html"].includes(pathname)) return;
  // An explicit arrival wins over a stale preference on the other domain.
  if (new URLSearchParams(search).get("region") === current) return;
  try {
    const result = await port.lookup(`${AUTH_ORIGINS[current]}/region`);
    if (!result || typeof result !== "object" || !("region" in result)) return;
    const target = result.region;
    if ((target !== "ru" && target !== "global") || target === current) return;
    const url = new URL("/", SITES[target]);
    // Read at navigation time: the visitor can change language during the lookup.
    const lang = port.language().toLowerCase().split(/[-_]/)[0] || "en";
    url.searchParams.set("lang", LANGUAGES.has(lang) ? lang : "en");
    url.searchParams.set("region", target);
    const arrival = new URLSearchParams(search);
    for (const field of CAMPAIGN_FIELDS) {
      const value = arrival.get(field);
      if (value && CAMPAIGN_VALUE.test(value)) url.searchParams.set(field, value);
    }
    if (/^#[a-z][a-z0-9-]{0,39}$/.test(hash)) url.hash = hash;
    // Do not copy arbitrary query strings, identifiers or fragments between regions.
    port.replace(url.href);
  } catch { /* Unknown/unavailable country must not interrupt the opened site. */ }
}

export function installMarketingRegionRouting(view: Window): void {
  if (import.meta.env.VITE_BB_REGIONAL_ROUTING_ENABLED !== "true") return;
  void routeMarketingRegion({
    location: view.location,
    language: () => view.document.documentElement.lang,
    lookup: async url => {
      const response = await view.fetch(url, { credentials: "include", cache: "no-store",
        referrerPolicy: "no-referrer", signal: AbortSignal.timeout(2000) });
      if (!response.ok) return null;
      return response.json();
    },
    replace: url => view.location.replace(url),
  });
}
